import React, { useState, useEffect } from 'react';
import {
  Box, AppBar, Toolbar, IconButton, Typography, TextField, Button, MenuItem,
  Accordion, AccordionSummary, AccordionDetails, Alert, Stack, ToggleButton,
  ToggleButtonGroup, Chip, Paper, Divider, CircularProgress, List, ListItem,
  ListItemButton, ListItemText, Radio,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { tasksAPI } from '../../services/api';
import { DISCIPLINES, STATUS_LABELS, STATUS_COLORS } from './taskConstants';

const isoDate = (d) => d.toISOString().slice(0, 10);

/**
 * Alta y toma de tareas para el jefe de equipo.
 *
 * Lo primero es la orden de fabricacion. En cuanto se elige salen sus tareas y
 * se toca una: eso es lo que hay que hacer, y el formulario de abajo sirve para
 * ponerle tecnico, fecha y horas. Si la que hace falta no esta en la lista, se
 * elige "Otra tarea" y se escribe a mano, que es cuando se crea una nueva.
 */
export default function CreateTask({ onDone, onCancel }) {
  const [orders, setOrders] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [orderTasks, setOrderTasks] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [title, setTitle] = useState('');
  const [orderId, setOrderId] = useState('');
  const [pickedTaskId, setPickedTaskId] = useState(null);   // null = tarea nueva
  const [assignee, setAssignee] = useState('');
  const [dateChoice, setDateChoice] = useState('today');
  const [customDate, setCustomDate] = useState(isoDate(new Date()));
  const [hours, setHours] = useState('');

  // Campos plegados
  const [description, setDescription] = useState('');
  const [discipline, setDiscipline] = useState('');
  const [location, setLocation] = useState('');
  const [safetyNotes, setSafetyNotes] = useState('');
  const [priority, setPriority] = useState('normal');

  useEffect(() => {
    tasksAPI.openOrders().then((r) => setOrders(r.data)).catch(() => {});
  }, []);

  // El equipo depende de la OF elegida: se recarga al cambiarla
  useEffect(() => {
    tasksAPI.assignableTechnicians(orderId || undefined)
      .then((r) => {
        const team = r.data.team || [];
        const rest = (r.data.all || []).filter((t) => !t.in_team);
        setTechnicians([...team, ...rest]);
      })
      .catch(() => {});
  }, [orderId]);

  // Tareas de la orden: son las que se pueden elegir
  useEffect(() => {
    setPickedTaskId(null);
    if (!orderId) {
      setOrderTasks(null);
      return;
    }
    setOrderTasks('loading');
    tasksAPI.list({ manufacturing_order_id: orderId })
      .then((r) => setOrderTasks(r.data))
      .catch(() => setOrderTasks([]));
  }, [orderId]);

  /** Elegir una tarea de la lista trae sus datos al formulario. */
  const pickTask = (task) => {
    if (!task) {                      // "Otra tarea": se vacia y se crea nueva
      setPickedTaskId(null);
      setTitle('');
      setHours('');
      setDescription(''); setDiscipline(''); setLocation('');
      setSafetyNotes(''); setPriority('normal');
      return;
    }
    setPickedTaskId(task.id);
    setTitle(task.title || '');
    setAssignee(task.assigned_technician_id || '');
    setHours(task.estimated_hours != null ? String(task.estimated_hours) : '');
    setDescription(task.description || '');
    setDiscipline(task.discipline || '');
    setLocation(task.location || '');
    setSafetyNotes(task.safety_notes || '');
    setPriority(task.priority || 'normal');
    if (task.planned_start) {
      setDateChoice('custom');
      setCustomDate(task.planned_start);
    }
  };

  const plannedStart = () => {
    const today = new Date();
    if (dateChoice === 'today') return isoDate(today);
    if (dateChoice === 'tomorrow') {
      const d = new Date(today);
      d.setDate(d.getDate() + 1);
      return isoDate(d);
    }
    return customDate;
  };

  const submit = async () => {
    if (!title.trim()) {
      setError('Pon un titulo a la tarea');
      return;
    }
    setBusy(true);
    setError('');
    const fields = {
      title: title.trim(),
      planned_start: plannedStart(),
      estimated_hours: hours ? parseFloat(hours) : null,
      description: description || null,
      discipline: discipline || null,
      location: location || null,
      safety_notes: safetyNotes || null,
      priority,
    };
    try {
      if (pickedTaskId) {
        // Tarea que ya existe: no se duplica, se actualiza y se asigna
        await tasksAPI.update(pickedTaskId, fields);
        await tasksAPI.assign(pickedTaskId, {
          technician_id: assignee || null,
          planned_start: fields.planned_start,
        });
        onDone(pickedTaskId);
        return;
      }
      const res = await tasksAPI.create({
        ...fields,
        manufacturing_order_id: orderId || null,
        assigned_technician_id: assignee || null,
      });
      onDone(res.data.id);
    } catch (err) {
      setError(err.response?.data?.detail
        || (pickedTaskId ? 'No se ha podido asignar la tarea' : 'No se ha podido crear la tarea'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ pb: 12 }}>
      <AppBar position="sticky" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={onCancel} aria-label="Cerrar"><CloseIcon /></IconButton>
          <Typography variant="subtitle1" fontWeight={600}>
            {pickedTaskId ? 'Asignar tarea' : 'Nueva tarea'}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Stack spacing={2}>
          <TextField select fullWidth label="Orden de fabricacion" value={orderId} autoFocus
            onChange={(e) => setOrderId(e.target.value)}
            helperText="Opcional: puede quedar sin orden y asignarsela la oficina">
            <MenuItem value="">
              <em>Sin orden</em>
            </MenuItem>
            {orders.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.code} — {o.name || o.project_name}
              </MenuItem>
            ))}
          </TextField>

          {/* Lo que ya hay en esa orden, para no repetir trabajo */}
          {orderTasks === 'loading' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {Array.isArray(orderTasks) && (
            <Paper variant="outlined">
              <Typography variant="subtitle2" sx={{ px: 1.5, pt: 1.5 }}>
                Que tarea vas a hacer
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, pb: 1, display: 'block' }}>
                {orderTasks.length === 0
                  ? 'Esta orden no tiene todavia ninguna tarea.'
                  : 'Toca la tarea de la orden que vas a hacer.'}
              </Typography>
              <Divider />
              <List disablePadding>
                {orderTasks.map((t) => (
                  <ListItem key={t.id} disablePadding divider
                    secondaryAction={
                      <Chip size="small" label={STATUS_LABELS[t.status] || t.status}
                        color={STATUS_COLORS[t.status] || 'default'} />
                    }>
                    <ListItemButton selected={pickedTaskId === t.id}
                      onClick={() => pickTask(t)} sx={{ py: 1 }}>
                      <Radio edge="start" size="small" tabIndex={-1} disableRipple
                        checked={pickedTaskId === t.id} />
                      <ListItemText
                        primary={t.title}
                        secondary={`${t.code}${t.assignee?.name ? ` · ${t.assignee.name}` : ' · sin asignar'}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
                <ListItem disablePadding>
                  <ListItemButton selected={pickedTaskId === null} onClick={() => pickTask(null)}
                    sx={{ py: 1 }}>
                    <Radio edge="start" size="small" tabIndex={-1} disableRipple
                      checked={pickedTaskId === null} />
                    <ListItemText primary="Otra tarea (escribirla a mano)"
                      primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }} />
                  </ListItemButton>
                </ListItem>
              </List>
            </Paper>
          )}

          <TextField fullWidth required label="Que hay que hacer" value={title}
            onChange={(e) => setTitle(e.target.value)}
            helperText={pickedTaskId
              ? 'Tarea que ya existe: si cambias el texto, se renombra'
              : 'Titulo de la tarea nueva'} />

          <TextField select fullWidth label="Asignar a" value={assignee}
            onChange={(e) => setAssignee(e.target.value)}>
            <MenuItem value=""><em>Sin asignar</em></MenuItem>
            {technicians.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.name}
                {t.in_team && <Chip size="small" label="en la OF" sx={{ ml: 1 }} />}
              </MenuItem>
            ))}
          </TextField>

          <Box>
            <Typography variant="caption" color="text.secondary">Cuando</Typography>
            <ToggleButtonGroup exclusive fullWidth value={dateChoice} size="large"
              onChange={(_, v) => v && setDateChoice(v)} sx={{ mt: 0.5 }}>
              <ToggleButton value="today" sx={{ height: 48 }}>Hoy</ToggleButton>
              <ToggleButton value="tomorrow" sx={{ height: 48 }}>Manana</ToggleButton>
              <ToggleButton value="custom" sx={{ height: 48 }}>Elegir</ToggleButton>
            </ToggleButtonGroup>
            {dateChoice === 'custom' && (
              <TextField fullWidth type="date" value={customDate} sx={{ mt: 1.5 }}
                onChange={(e) => setCustomDate(e.target.value)} />
            )}
          </Box>

          <TextField fullWidth label="Horas estimadas" value={hours}
            onChange={(e) => setHours(e.target.value.replace(',', '.'))}
            inputProps={{ inputMode: 'decimal' }} />
        </Stack>

        <Accordion elevation={0} sx={{ mt: 2, '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
            <Typography variant="subtitle2">Mas detalles</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Stack spacing={2}>
              <TextField fullWidth multiline rows={3} label="Descripcion" value={description}
                onChange={(e) => setDescription(e.target.value)} />
              <TextField select fullWidth label="Disciplina" value={discipline}
                onChange={(e) => setDiscipline(e.target.value)}>
                <MenuItem value=""><em>Sin definir</em></MenuItem>
                {DISCIPLINES.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </TextField>
              <TextField fullWidth label="Ubicacion" value={location}
                onChange={(e) => setLocation(e.target.value)} />
              <TextField fullWidth multiline rows={2} label="Notas de seguridad"
                value={safetyNotes} onChange={(e) => setSafetyNotes(e.target.value)} />
              <TextField select fullWidth label="Prioridad" value={priority}
                onChange={(e) => setPriority(e.target.value)}>
                {['baja', 'normal', 'alta', 'urgente'].map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </TextField>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box sx={{
        position: 'fixed', bottom: 56, left: 0, right: 0, p: 1.5,
        backgroundColor: 'background.paper', borderTop: '1px solid', borderColor: 'divider',
      }}>
        <Button fullWidth variant="contained" size="large" disabled={busy || !title.trim()}
          onClick={submit} sx={{ height: 52, fontSize: 16 }}>
          {busy
            ? 'Guardando...'
            : pickedTaskId ? 'Asignar esta tarea' : 'Crear tarea'}
        </Button>
      </Box>
    </Box>
  );
}
